import { map, find, filter } from 'lodash-es';
import { defineStore } from 'pinia';
import useExportQueueStore from '@/stores/manuscripts/exportQueue';
import {
  MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX,
} from '@/lib/constants/manuscripts/api';
import * as store from '@/lib/constants/manuscripts/store';

const useExportStore = defineStore('manuscript-export', {
  state: () => ({
    selectedExports: [],
  }),
  getters: {
    selectedExportIds() {
      return map(this.selectedExports, (exp) => exp.export_id);
    },
    exportItems() {
      const exportQueueStore = useExportQueueStore();

      return map(exportQueueStore.queueItems, (item) => {
        const { export_id: exportId } = item;
        const isSelected = this.selectedExportIds.includes(exportId);
        const downloadLink = this.downloadLink(item);
        const canDownload = this.canDownload(item);

        return {
          ...item,
          isSelected,
          canDownload,
          canSelect: canDownload && (isSelected || this.canAddToSelection),
          downloadLink,
        };
      });
    },
    canAddToSelection() {
      const incrementedCount = (this.selectedExportIds?.length || 0) + 1;

      return (incrementedCount <= store.MAX_SELECTED_EXPORTS)
        && this.hasUnselectedItems;
    },
    hasUnselectedItems() {
      const exportQueueStore = useExportQueueStore();

      return !!find(exportQueueStore.queueItems, (item) => {
        const canDownload = this.canDownload(item);
        const isSelected = this.selectedExportIds.includes(item.export_id);

        return canDownload && !isSelected;
      });
    },
    canSelectAll() {
      return this.allSelected || this.canAddToSelection;
    },
    isMaxSelected() {
      return this.selectedExportIds?.length >= store.MAX_SELECTED_EXPORTS;
    },
    selectedItems() {
      return filter(this.downloadableItems, (item) => item.isSelected);
    },
    allSelected() {
      return !!this.downloadableItems?.length
        && (
          this.downloadableItems.every((item) => item.isSelected)
            || (this.isMaxSelected && !!this.selectedItems?.length)
        );
    },
    downloadableItems() {
      return filter(this.exportItems, (i) => i.canDownload);
    },
    canViewManuscripts() {
      const viewManuscriptStatuses = [
        store.QUEUE_ITEM_STATUS_COMPLETE,
        store.QUEUE_ITEM_STATUS_ERROR,
        store.QUEUE_ITEM_STATUS_EXPIRED,
      ];

      return (queueItem) => queueItem?.export_type === store.EXPORT_TYPE_BULK_GENERATE
          && viewManuscriptStatuses.includes(queueItem?.status);
    },
    canDownload() {
      return (queueItem) => !!this.downloadLink(queueItem);
    },
    downloadLink() {
      return (queueItem) => {
        const exportStatus = queueItem?.status;

        if (exportStatus !== store.QUEUE_ITEM_STATUS_COMPLETE) {
          return null;
        }

        return this.downloadUrl(queueItem?.export_id);
      };
    },
    downloadUrl() {
      return (exportId) => {
        const exportQueueStore = useExportQueueStore();

        const searchQueueItems = [
          ...exportQueueStore.queueItems,
          ...this.selectedExports,
        ];

        const queueItem = searchQueueItems.find(
          (item) => item.export_id === exportId,
        );

        if (!queueItem) {
          return null;
        }

        const appDomain = this.domains.appDomain.replace('/learn', '');

        if (queueItem.export_type === store.EXPORT_TYPE_BULK_GENERATE) {
          return `${appDomain}${URL_PREFIX}/bulk-generate/${queueItem.bulk_generation_id}/download`;
        }

        return `${appDomain}/suite/api/manuscript/exports/download/${exportId}`;
      };
    },
  },
  actions: {
    async [store.DOWNLOAD_SELECTED_EXPORTS]() {
      const downloads = this.selectedExportIds.map(async (exportId) => {
        try {
          const { pathname } = new URL(this.downloadUrl(exportId));
          const response = await this.appApi(pathname, { responseType: 'arraybuffer' });
          const contentType = response.headers.get('content-type') || 'application/octet-stream';
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = URL.createObjectURL(blob);

          const disposition = response.headers.get('content-disposition') || '';
          const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
          const filename = filenameMatch ? decodeURIComponent(filenameMatch[1].trim()) : exportId;

          const a = document.createElement('a');

          a.href = blobUrl;
          a.download = filename;
          a.style.display = 'none';

          document.body.appendChild(a);

          a.click();

          document.body.removeChild(a);

          URL.revokeObjectURL(blobUrl);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Failed to download export ${exportId}:`, error);
        }
      });

      await Promise.all(downloads);

      this[store.CLEAR_SELECTED_EXPORTS]();
    },
    [store.CLEAR_SELECTED_EXPORTS]() {
      this.selectedExports = [];
    },
    [store.TOGGLE_EXPORT_ITEM](queueItem) {
      const { export_id: exportId } = queueItem;
      const index = this.selectedExports.findIndex((exp) => exp.export_id === exportId);

      if (index === -1 && this.canAddToSelection) {
        this.selectedExports.push(queueItem);
      } else {
        this.selectedExports.splice(index, 1);
      }
    },
    [store.TOGGLE_ALL_EXPORTS]({ isSelected }) {
      const exportQueueStore = useExportQueueStore();

      exportQueueStore.queueItems.forEach((queueItem) => {
        const { export_id: exportId } = queueItem;
        const index = this.selectedExports.findIndex((exp) => exp.export_id === exportId);

        if (
          isSelected
          && index === -1
          && this.canDownload(queueItem)
          && this.canAddToSelection
        ) {
          this.selectedExports.push(queueItem);
        } else if (!isSelected && index !== -1) {
          this.selectedExports.splice(index, 1);
        }
      });
    },
  },
});

export default useExportStore;
